import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { ArrowRight, Mail, KeyRound, ArrowLeft, Smartphone } from "lucide-react";
import ChannelPicker from "./portal/ChannelPicker";

const PORTAL_BG = "https://static.prod-images.emergentagent.com/jobs/d0292276-e84a-42f6-aad0-4f23c6fd8a65/images/55a57bc52f06288ab5cfc331366ab176d1241dd60b6402e36416b1dbe8fa5282.png";

const PORTAL_BG_STYLE = {
  backgroundImage: `linear-gradient(rgba(10,10,12,0.78), rgba(10,10,12,0.92)), url(${PORTAL_BG})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
};

export default function Portal() {
  const { settings } = useSettings();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // step: "code" | "email" | "otp"
  const [step, setStep] = useState("code");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [attemptId, setAttemptId] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState(null);
  const [sentChannel, setSentChannel] = useState("email");
  const isOwner = channels.length > 0;

  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");

  const codeRef = useRef(null);
  const emailRef = useRef(null);
  const otpRef = useRef(null);

  const loadMethod = useCallback(async (codeValue) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/codes/${encodeURIComponent(codeValue)}/access-method`);
      const ch = data.method === "owner" ? data.channels || [] : [];
      setChannels(ch);
      setChannel(ch[0]?.type || null);
      setStep("email");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't check this code. Please try again.");
      setStep("code");
    } finally {
      setLoading(false);
    }
  }, []);

  // NFC deep-link: /?code=XXX pre-fills the code and jumps straight to the verification step
  useEffect(() => {
    const urlCode = (params.get("code") || "").trim().toUpperCase();
    if (urlCode) {
      setCode(urlCode);
      loadMethod(urlCode);
      const key = `scan:${urlCode}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        api.post("/codes/scan", { code: urlCode, source: "nfc" }).catch(() => {});
      }
    }
  }, [params, loadMethod]);

  // auto-focus the active input
  useEffect(() => {
    const t = setTimeout(() => {
      if (step === "code") codeRef.current?.focus();
      else if (step === "email") emailRef.current?.focus();
      else if (step === "otp") otpRef.current?.focus();
    }, 300);
    return () => clearTimeout(t);
  }, [step]);

  const requestOtp = useCallback(async (codeValue, emailValue, channelValue) => {
    const v = (codeValue || "").trim().toUpperCase();
    const em = (emailValue || "").trim();
    if (!v) return;
    setLoading(true);
    setError("");
    try {
      const body = em ? { code: v, email: em } : { code: v, channel: channelValue };
      const { data } = await api.post("/codes/request-otp", body);
      setSentChannel(data.channel || "email");
      setAttemptId(data.attempt_id);
      setEmailMasked(data.email_masked);
      setStep("otp");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't send the code. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (otpValue) => {
    const o = (otpValue || "").trim();
    if (o.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/codes/verify-otp", { attempt_id: attemptId, otp: o });
      if (data.success) {
        setUnlocking(true);
        setTimeout(() => {
          navigate(`/bottle?t=${encodeURIComponent(data.view_token)}`);
        }, 700);
      } else {
        setError(data.message || "Verification failed. Please try again.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Verification failed. Please try again.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  }, [attemptId, navigate]);

  const onSubmitCode = (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    loadMethod(code.trim());
  };

  const onSubmitEmail = (e) => {
    e?.preventDefault();
    requestOtp(code, isOwner ? null : email, channel);
  };

  const onSubmitOtp = (e) => {
    e?.preventDefault();
    verifyOtp(otp);
  };

  const resendOtp = () => {
    setOtp("");
    setError("");
    requestOtp(code, isOwner ? null : email, channel);
  };

  const back = () => {
    setError("");
    if (step === "otp") setStep("email");
    else if (step === "email") setStep("code");
  };

  return (
    <div
      data-testid="portal-page"
      className="min-h-screen w-full relative flex flex-col items-center justify-center overflow-hidden grain"
      style={PORTAL_BG_STYLE}
    >
      <div className="aura" />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-6 sm:px-10 py-6 flex items-center justify-between z-10">
        <a href="/" className="flex items-center gap-3 fade-up" data-testid="brand-link">
          <img src={settings.logo_url} alt={settings.brand_name} className="w-9 h-9 object-contain" style={{ mixBlendMode: "lighten" }} />
          <span className="font-display text-lg tracking-wide hidden sm:inline">{settings.brand_name}</span>
        </a>
        <a href="/admin/login" data-testid="admin-link" className="overline text-[var(--vault-text-muted)] hover:text-white transition-colors">Admin</a>
      </div>

      {/* Card */}
      <div
        className={`relative z-10 glass rounded-2xl px-8 sm:px-14 py-14 sm:py-16 max-w-2xl w-[92%] text-center shadow-2xl transition-all duration-500 ${unlocking ? "scale-95 opacity-0" : "scale-100 opacity-100"} fade-up`}
        data-testid="portal-card"
      >
        <div className="flex justify-center mb-8">
          <img src={settings.logo_url} alt={settings.brand_name} className="w-24 h-24 sm:w-28 sm:h-28 object-contain" style={{ mixBlendMode: "lighten" }} data-testid="portal-logo" />
        </div>

        {/* STEP 1 — CODE */}
        {step === "code" && (
          <form onSubmit={onSubmitCode} className="fade-up" data-testid="step-code">
            <p className="overline text-[var(--vault-text-muted)] mb-4">{settings.portal_overline}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-4">
              {settings.portal_heading}
            </h1>
            <p className="text-[var(--vault-text-muted)] text-base sm:text-lg mb-10 max-w-md mx-auto">
              {settings.portal_subtitle}
            </p>
            <input
              ref={codeRef}
              data-testid="code-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 32))}
              placeholder="——— ———"
              autoComplete="off"
              spellCheck={false}
              className={`code-input ${error ? "error" : ""}`}
              disabled={loading}
            />
            <div className="min-h-[28px] mt-4">
              {error && <p data-testid="error-message" className="text-sm font-mono-vault tracking-wider" style={{ color: "#FCA5A5" }}>{error}</p>}
            </div>
            <button type="submit" data-testid="code-next-btn" disabled={!code.trim() || loading} className="btn-gold mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-md">
              {loading ? <span className="spin" /> : <><span>Continue</span><ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}

        {/* STEP 2 — EMAIL */}
        {step === "email" && (
          <form onSubmit={onSubmitEmail} className="fade-up" data-testid="step-email">
            {channel === "sms" ? <Smartphone className="w-10 h-10 mx-auto mb-5" style={{ color: "var(--vault-gold)" }} /> : <Mail className="w-10 h-10 mx-auto mb-5" style={{ color: "var(--vault-gold)" }} />}
            <p className="overline text-[var(--vault-text-muted)] mb-3">Bottle · <span className="font-mono-vault" style={{ color: "var(--vault-gold)" }}>{code}</span></p>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight mb-4">{isOwner ? "Verify it's you" : "Request your access code"}</h1>
            {isOwner ? (
              <ChannelPicker channels={channels} value={channel} onChange={setChannel} disabled={loading} />
            ) : (
              <p className="text-[var(--vault-text-muted)] text-sm sm:text-base mb-10 max-w-md mx-auto">
                Enter your email to receive a 6-digit code. It is valid for 10 minutes.
              </p>
            )}
            {!isOwner && <input
              ref={emailRef}
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full max-w-md mx-auto block px-4 py-3 rounded-md outline-none text-center"
              style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
              disabled={loading}
              required
            />}
            <div className="min-h-[28px] mt-4">
              {error && <p data-testid="error-message" className="text-sm font-mono-vault tracking-wider" style={{ color: "#FCA5A5" }}>{error}</p>}
            </div>
            <div className="flex items-center justify-center gap-2 mt-6">
              <button type="button" onClick={back} className="btn-ghost inline-flex items-center gap-2 px-5 py-3 rounded-md" data-testid="email-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button type="submit" disabled={loading || (isOwner ? !channel : !email.trim())} data-testid="email-send-btn" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-md">
                {loading ? <span className="spin" /> : <>Send code <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}

        {/* STEP 3 — OTP */}
        {step === "otp" && (
          <form onSubmit={onSubmitOtp} className="fade-up" data-testid="step-otp">
            <KeyRound className="w-10 h-10 mx-auto mb-5" style={{ color: "var(--vault-gold)" }} />
            <p className="overline text-[var(--vault-text-muted)] mb-3">Bottle · <span className="font-mono-vault" style={{ color: "var(--vault-gold)" }}>{code}</span></p>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight mb-4">Enter your access code</h1>
            <p className="text-[var(--vault-text-muted)] text-sm sm:text-base mb-8 max-w-md mx-auto">
              We've sent a 6-digit code {sentChannel === "sms" ? "by SMS" : ""} to <span className="text-white" data-testid="otp-destination">{emailMasked}</span>. {sentChannel === "sms" ? "Check your messages." : "Check your inbox and spam folder."}
            </p>
            <input
              ref={otpRef}
              data-testid="otp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              autoComplete="one-time-code"
              className={`code-input ${error ? "error" : ""}`}
              style={{ letterSpacing: "0.5em", fontSize: "clamp(2rem, 7vw, 3rem)" }}
              disabled={loading || unlocking}
            />
            <div className="min-h-[28px] mt-4">
              {error && <p data-testid="error-message" className="text-sm font-mono-vault tracking-wider" style={{ color: "#FCA5A5" }}>{error}</p>}
            </div>
            <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
              <button type="button" onClick={back} className="btn-ghost inline-flex items-center gap-2 px-5 py-3 rounded-md" data-testid="otp-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button type="submit" disabled={loading || unlocking || otp.length !== 6} data-testid="otp-verify-btn" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-md">
                {loading ? <span className="spin" /> : <>Unlock <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
            <p className="mt-5 text-xs text-[var(--vault-text-muted)]">
              Didn't receive it?{" "}
              <button type="button" onClick={resendOtp} className="underline hover:text-white" data-testid="otp-resend-btn" disabled={loading}>
                Resend
              </button>
            </p>
          </form>
        )}
      </div>

      <p className="absolute bottom-6 left-0 right-0 px-4 text-center overline text-[var(--vault-text-faint)] z-10 flex flex-wrap justify-center gap-x-2 gap-y-1">
        <span>{settings.portal_footer}</span>
        <span className="hidden sm:inline">·</span>
        <a href="/verify" className="hover:text-white transition-colors" data-testid="portal-verify-link">Verify a code</a>
      </p>
    </div>
  );
}
